/*
 * Fork of:
 *
 * https://github.com/backstage/backstage/blob/master/plugins/scaffolder-react/src/next/components/ReviewState/ReviewState.tsx
 *
 * Changes:
 *
 * - using own fork of ./util
 * - override nested table styling to perform word-wrap on questions that can be very wordy in SNDT
 */
import { JsonObject, JsonValue } from '@backstage/types'
import { StructuredMetadataTable } from '@backstage/core-components'
import {
  ParsedTemplateSchema,
  ReviewStateProps
} from '@backstage/plugin-scaffolder-react/alpha'
import { Draft07 as JSONSchema } from 'json-schema-library'
import { findSchemaForKey, isJsonObject } from './util'
import { makeStyles } from '@material-ui/core'

function processSchema(
  key: string,
  value: JsonValue | undefined,
  schema: ParsedTemplateSchema,
  formState: JsonObject
): [string, JsonValue | undefined][] {
  const parsedSchema = new JSONSchema(schema.mergedSchema)
  const definitionInSchema = parsedSchema.getSchema({
    pointer: `#/${key}`,
    data: formState
  })

  const name =
    definitionInSchema?.['ui:backstage']?.review?.name ?? definitionInSchema?.title ?? key

  if (definitionInSchema) {
    const backstageReviewOptions = definitionInSchema['ui:backstage']?.review
    if (backstageReviewOptions) {
      if (backstageReviewOptions.mask) {
        return [[name, backstageReviewOptions.mask]]
      }
      if (backstageReviewOptions.show === false) {
        return []
      }
    }

    if (
      definitionInSchema['ui:widget'] === 'password' ||
      definitionInSchema['ui:field']?.toLocaleLowerCase('en-us') === 'secret'
    ) {
      return [[name, '******']]
    }

    if (definitionInSchema.enum && definitionInSchema.enumNames) {
      return [
        [
          name,
          definitionInSchema.enumNames[definitionInSchema.enum.indexOf(value)] || value
        ]
      ]
    }

    if (backstageReviewOptions?.explode !== false && isJsonObject(value)) {
      // Recurse nested objects
      return Object.entries(value).flatMap(([nestedKey, nestedValue]) =>
        processSchema(`${key}/${nestedKey}`, nestedValue, schema, formState)
      )
    }
  }

  return [[name, value]]
}

const useStyles = makeStyles({
  wrapper: {
    '& td:first-child': {
      whiteSpace: 'normal' // <-- this fixes stock Backstage behavior
    }
  }
})

export function ReviewState(props: ReviewStateProps) {
  const styles = useStyles()

  const reviewData = Object.fromEntries(
    Object.entries(props.formState)
      .flatMap(([key, value]) => {
        const schema = findSchemaForKey(key, props.schemas, props.formState)
        return schema
          ? processSchema(key, value, schema, props.formState)
          : [[key, value]]
      })
      .filter(prop => prop.length > 0)
  )

  const options = {
    titleFormat: (key: string) => key
  }

  return (
    <div className={styles.wrapper}>
      <StructuredMetadataTable metadata={reviewData} options={options} />
    </div>
  )
}
